import { useState, useCallback, useEffect, useMemo } from "react";
import { apiService } from "@/core/api";
import { API_ENDPOINTS } from "@/core/constants/endpoints";
import { fetchUserBasicInfoRequested } from "@/features/user/store";
import { organizationDataChanged } from "@features/settings/store";

interface UserData {
  id: string;
  logo_uri: string;
  username: string;
  email: string;
  firstname: string;
  lastname: string;
  description: string;
  job_title: string;
  is_followed_by_me: boolean;
  is_following_me: boolean;
  is_me: boolean;
}

interface InvitationRequest {
  action_id: string;
  uid: string;
  user_data: UserData;
}

interface InvitationsRequestsResponse {
  request: InvitationRequest[];
  invitation: InvitationRequest[];
}

export interface CombinedInvitationRequest extends InvitationRequest {
  type: "request" | "invitation";
  invite_date?: string;
}

export const InvitationsViewModel = () => {
  const [invitationsRequests, setInvitationsRequests] =
    useState<InvitationsRequestsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchInvitations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.get<InvitationsRequestsResponse>(
        API_ENDPOINTS.PARTNERS.INVITATIONS_REQUESTS
      );
      setInvitationsRequests(response);
    } catch (error) {
      console.error("Failed to fetch invitations and requests:", error);
      setInvitationsRequests(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  const handleRequestAction = useCallback(
    async (actionId: string, action: "accept" | "deny") => {
      try {
        await apiService.post(API_ENDPOINTS.PARTNERS.ACTION_ANSWER, {
          action_answer: action,
          action_id: actionId,
        });
        await fetchInvitations();
        if (action === "accept") {
          fetchUserBasicInfoRequested();
          organizationDataChanged();
        }
        return true;
      } catch (error) {
        console.error(`Failed to ${action} request:`, error);
        throw error;
      }
    },
    [fetchInvitations]
  );

  const withdrawInvitation = useCallback(
    async (uid: string) => {
      try {
        await apiService.post(API_ENDPOINTS.PARTNERS.ACTION, {
          action_type: "invitation",
          action_mode: "remove",
          target_id: uid,
        });
        await fetchInvitations();
        return true;
      } catch (error) {
        console.error("Failed to withdraw invitation:", error);
        throw error;
      }
    },
    [fetchInvitations]
  );

  const requests: CombinedInvitationRequest[] = useMemo(() => {
    if (!invitationsRequests) return [];
    return (invitationsRequests.request || []).map((req) => ({
      ...req,
      type: "request" as const,
    }));
  }, [invitationsRequests]);

  const invitations: CombinedInvitationRequest[] = useMemo(() => {
    if (!invitationsRequests) return [];
    return (invitationsRequests.invitation || []).map((inv) => ({
      ...inv,
      type: "invitation" as const,
      invite_date: new Date().toLocaleDateString(),
    }));
  }, [invitationsRequests]);

  return {
    requests,
    invitations,
    loading,
    handleRequestAction,
    withdrawInvitation,
    fetchInvitations,
  };
};
